import React, { useContext } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import HistoryIcon from '@material-ui/icons/History';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import format from 'date-fns/format';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { ProductEditHistoryContext } from './ProductEditHistoryContext';

interface IFieldHistoryIconProps {
  name: string;
  isBoolean?: boolean;
}

const useStyles = makeStyles(() => ({
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(-360deg)',
    },
  },
  loadingIcon: {
    animation: '$spin 1.5s linear infinite',
  },
}));

const HistoryTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#000000',
    color: '#ffffff',
    maxWidth: 600,
    fontSize: '12px',
    border: '1px solid #334155',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    padding: '8px 12px',
    borderRadius: '8px',
  },
}))(Tooltip);

export const FieldHistoryIcon = ({ name, isBoolean = false }: IFieldHistoryIconProps) => {
  const classes = useStyles();
  const context = useContext(ProductEditHistoryContext);
  if (!context) return null;

  const { historyData, historyLoading, expandHistoryAndScroll } = context;

  const findCreateVersionDetails = (data: any) => {
    if (!data?.productEditHistory) return null;
    for (const entries of Object.values(data.productEditHistory)) {
      if (Array.isArray(entries)) {
        const createEntry = entries.find((entry: any) => entry?.type === 'CREATE');
        if (createEntry) {
          return {
            versionCreated: createEntry.versionCreated,
            updatedBy: createEntry.updatedBy,
          };
        }
      }
    }
    return null;
  };

  const rawHistoryEntries = historyData?.productEditHistory?.[name] || [];
  const hasCreate = rawHistoryEntries.some((entry: any) => entry?.type === 'CREATE');
  
  let historyEntries = [...rawHistoryEntries];
  if (!hasCreate) {
    const createDetails = findCreateVersionDetails(historyData);
    if (createDetails) {
      historyEntries.push({
        type: 'CREATE',
        versionCreated: createDetails.versionCreated,
        updatedBy: createDetails.updatedBy,
        value: null,
        wasAbsent: true,
      });
    }
  }

  const recentHistory = historyEntries.slice(0, 3);

  const hasComplexValue = historyEntries.some((entry: any) => 
    entry.value !== null && typeof entry.value === 'object' && !Array.isArray(entry.value)
  );

  if (!historyLoading && historyEntries.length === 0) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    expandHistoryAndScroll(name);
  };

  const renderTooltipContent = () => {
    if (historyLoading) {
      return <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>Laddar historik...</span>;
    }
    if (!historyEntries || historyEntries.length === 0) {
      return <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>ingen redigeringshistorik</span>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {recentHistory.map((entry: any, index: number) => {
          const dateStr = entry.versionCreated
            ? format(Number(entry.versionCreated), 'yyyy-MM-dd HH:mm')
            : '-';
          
          let displayValue = entry.value;
          let isComplex = false;
          let isNotSet = false;
          
          if (displayValue !== null && typeof displayValue === 'object' && !Array.isArray(displayValue)) {
            displayValue = '[Ändrad] (klicka för detaljer nedan)';
            isComplex = true;
          } else if (displayValue === true) {
            displayValue = 'Ja';
          } else if (displayValue === false) {
            displayValue = 'Nej';
          } else if (displayValue === null || displayValue === undefined) {
            displayValue = isBoolean ? '(ej valt)' : '(ej angivet)';
            isNotSet = true;
          } else if (typeof displayValue === 'string' && displayValue.trim() === '') {
            displayValue = '(tom sträng)';
            isNotSet = true;
          } else if (Array.isArray(displayValue)) {
            displayValue = displayValue.join(', ');
          }

          const isValueBlank = entry.value === null || entry.value === undefined;
          const isValueEmpty = typeof entry.value === 'string' && entry.value.trim() === '';
          const canCopy = !isBoolean && !isComplex && !isValueBlank && !isValueEmpty && !entry.wasAbsent;

          const handleCopy = (e: React.MouseEvent) => {
            e.stopPropagation();
            navigator.clipboard.writeText(String(entry.value));
          };

          const typeText = entry.type === 'CREATE' ? 'skapad' : 'ändrad';
          const typeColor = entry.type === 'CREATE' ? '#4ade80' : '#60a5fa';

          return (
            <div 
              key={index} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                whiteSpace: 'nowrap',
                lineHeight: '1.4'
              }}
            >
              {canCopy ? (
                <Tooltip title="Kopiera" placement="top">
                  <FileCopyIcon 
                    onClick={handleCopy}
                    style={{ 
                      cursor: 'pointer', 
                      fontSize: '13px', 
                      color: '#94a3b8',
                      flexShrink: 0,
                      transition: 'color 0.2s',
                    }} 
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#ffffff'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#94a3b8'; }}
                  />
                </Tooltip>
              ) : (
                <div style={{ width: '13px', flexShrink: 0 }} />
              )}
              <span style={{ color: '#ffffff', fontWeight: 500 }}>
                <span style={{ color: '#94a3b8', fontWeight: 400 }}>{dateStr}</span>
                {entry.updatedBy && <span style={{ color: '#fbbf24', fontWeight: 400 }}>{` (${entry.updatedBy})`}</span>}
                {entry.type && <span style={{ color: typeColor, fontWeight: 400 }}>{` [${typeText}]`}</span>}
                <span style={{ color: '#94a3b8', fontWeight: 400 }}>: </span>
                {isNotSet ? (
                  <span style={{ fontStyle: 'italic', color: '#64748b', fontWeight: 400 }}>
                    {String(displayValue)}
                  </span>
                ) : (
                  String(displayValue)
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <HistoryTooltip title={renderTooltipContent()} interactive placement="top-start" enterDelay={0} leaveDelay={500}>
      <HistoryIcon 
        className={historyLoading ? classes.loadingIcon : ''}
        onClick={hasComplexValue ? handleClick : undefined}
        style={{ 
          marginRight: '8px', 
          cursor: hasComplexValue ? 'pointer' : 'default', 
          color: '#94a3b8',
          fontSize: '20px',
          verticalAlign: 'middle',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s'
        }} 
        onMouseEnter={(e) => { 
          const target = e.currentTarget as any;
          target.style.color = '#475569'; 
          target.style.transform = 'rotate(-360deg) scale(1.2)';
        }}
        onMouseLeave={(e) => { 
          const target = e.currentTarget as any;
          target.style.color = '#94a3b8'; 
          target.style.transform = 'rotate(0deg) scale(1)';
        }}
      />
    </HistoryTooltip>
  );
};
